import React, { useState } from 'react';
import { 
  ArrowLeft, Plus, Search, Edit3, Trash2, X, User, Users, Phone, Mail,
  Calendar, Clock, Check, AlertCircle, MoreVertical, Eye, EyeOff
} from 'lucide-react';
import { useApp } from '../context/AppContext';
import { RESOURCES, getResourcesForOffice, MODULES } from '../data/database';

const OperatorsPage = ({ onBack, onNavigate }) => {
  const { selectedOffice } = useApp();
  const officeId = selectedOffice?.id || 1;
  const officeModules = selectedOffice?.enabledModules || ['appointments'];
  
  // Determina il tipo di risorsa dal primo modulo abilitato
  const moduleWithResources = officeModules.find(m => MODULES[m]?.schema?.resourceTypes?.length > 0);
  const moduleConfig = MODULES[moduleWithResources] || {};
  
  // Get the resource type for this office (operators, trainers, etc.)
  const resourceInfo = getResourcesForOffice(officeId);
  const resourceTypeName = resourceInfo?.typeName || 'Operatore';
  const resourceTypeNamePlural = resourceInfo?.typeNamePlural || 'Operatori';
  
  // Initialize operators from database
  const [operators, setOperators] = useState(() => {
    const list = resourceInfo?.list || [];
    if (list.length > 0) return list;
    // Default operators for demo
    return [
      { id: 1, officeId, name: 'Marco', surname: 'Rossi', role: 'Senior', avatar: 'MR', color: 'emerald', phone: '+39 333 1234567', email: 'marco@example.it', isActive: true, specialties: [] },
      { id: 2, officeId, name: 'Sara', surname: 'Bianchi', role: 'Junior', avatar: 'SB', color: 'blue', phone: '+39 333 7654321', email: 'sara@example.it', isActive: true, specialties: [] },
    ];
  });
  
  const [searchQuery, setSearchQuery] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingOperator, setEditingOperator] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    surname: '',
    role: '',
    phone: '',
    email: '',
    color: 'emerald',
    specialties: [],
    isActive: true,
    workingDays: {
      monday: { enabled: true, start: '09:00', end: '18:00' },
      tuesday: { enabled: true, start: '09:00', end: '18:00' },
      wednesday: { enabled: true, start: '09:00', end: '18:00' },
      thursday: { enabled: true, start: '09:00', end: '18:00' },
      friday: { enabled: true, start: '09:00', end: '18:00' },
      saturday: { enabled: false, start: '09:00', end: '13:00' },
      sunday: { enabled: false, start: '', end: '' },
    }
  });
  
  const colorOptions = [
    { id: 'emerald', name: 'Verde', class: 'bg-emerald-500' },
    { id: 'blue', name: 'Blu', class: 'bg-blue-500' },
    { id: 'purple', name: 'Viola', class: 'bg-purple-500' },
    { id: 'orange', name: 'Arancione', class: 'bg-orange-500' },
    { id: 'pink', name: 'Rosa', class: 'bg-pink-500' },
    { id: 'cyan', name: 'Ciano', class: 'bg-cyan-500' },
  ];
  
  const dayNames = {
    monday: 'Lunedì',
    tuesday: 'Martedì',
    wednesday: 'Mercoledì',
    thursday: 'Giovedì',
    friday: 'Venerdì',
    saturday: 'Sabato',
    sunday: 'Domenica',
  };

  const filteredOperators = operators.filter(op => 
    op.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    op.surname?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    op.role?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const resetForm = () => {
    setFormData({
      name: '',
      surname: '',
      role: '',
      phone: '',
      email: '',
      color: 'emerald',
      specialties: [],
      isActive: true,
      workingDays: {
        monday: { enabled: true, start: '09:00', end: '18:00' },
        tuesday: { enabled: true, start: '09:00', end: '18:00' },
        wednesday: { enabled: true, start: '09:00', end: '18:00' },
        thursday: { enabled: true, start: '09:00', end: '18:00' },
        friday: { enabled: true, start: '09:00', end: '18:00' },
        saturday: { enabled: false, start: '09:00', end: '13:00' },
        sunday: { enabled: false, start: '', end: '' },
      }
    });
    setEditingOperator(null);
  };

  const openAddModal = () => {
    resetForm();
    setShowModal(true);
  };

  const openEditModal = (operator) => {
    setEditingOperator(operator);
    setFormData({
      name: operator.name || '',
      surname: operator.surname || '',
      role: operator.role || '',
      phone: operator.phone || '',
      email: operator.email || '',
      color: operator.color || 'emerald',
      specialties: operator.specialties || [],
      isActive: operator.isActive !== false,
      workingDays: operator.workingDays || {
        monday: { enabled: true, start: '09:00', end: '18:00' },
        tuesday: { enabled: true, start: '09:00', end: '18:00' },
        wednesday: { enabled: true, start: '09:00', end: '18:00' },
        thursday: { enabled: true, start: '09:00', end: '18:00' },
        friday: { enabled: true, start: '09:00', end: '18:00' },
        saturday: { enabled: false, start: '09:00', end: '13:00' },
        sunday: { enabled: false, start: '', end: '' },
      }
    });
    setShowModal(true);
  };

  const handleSave = () => {
    if (!formData.name.trim()) return;

    const operatorData = {
      id: editingOperator?.id || Date.now(),
      businessId,
      name: formData.name.trim(),
      surname: formData.surname.trim(),
      role: formData.role.trim(),
      avatar: `${formData.name.charAt(0)}${formData.surname?.charAt(0) || ''}`.toUpperCase(),
      phone: formData.phone.trim(),
      email: formData.email.trim(),
      color: formData.color,
      specialties: formData.specialties,
      isActive: formData.isActive,
      workingDays: formData.workingDays,
    };

    if (editingOperator) {
      setOperators(prev => prev.map(op => op.id === editingOperator.id ? operatorData : op));
    } else {
      setOperators(prev => [...prev, operatorData]);
    }

    setShowModal(false);
    resetForm();
  };

  const handleDelete = (id) => {
    setOperators(prev => prev.filter(op => op.id !== id));
    setShowDeleteConfirm(null);
  };

  const toggleActive = (id) => {
    setOperators(prev => prev.map(op => 
      op.id === id ? { ...op, isActive: !op.isActive } : op
    ));
  };

  const toggleWorkingDay = (day) => {
    setFormData(prev => ({
      ...prev,
      workingDays: {
        ...prev.workingDays,
        [day]: {
          ...prev.workingDays[day],
          enabled: !prev.workingDays[day].enabled
        }
      }
    }));
  };

  const updateWorkingHours = (day, field, value) => {
    setFormData(prev => ({
      ...prev,
      workingDays: {
        ...prev.workingDays,
        [day]: {
          ...prev.workingDays[day],
          [field]: value
        }
      }
    }));
  };

  const getColorClass = (color) => {
    const colorMap = {
      emerald: 'bg-emerald-500',
      blue: 'bg-blue-500',
      purple: 'bg-purple-500',
      orange: 'bg-orange-500',
      pink: 'bg-pink-500',
      cyan: 'bg-cyan-500',
    };
    return colorMap[color] || 'bg-emerald-500';
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      {/* Header */}
      <div className="bg-white border-b border-gray-100 sticky top-0 z-20">
        <div className="max-w-screen-2xl mx-auto">
          <div className="flex items-center gap-4 p-4">
            <button 
              onClick={onBack} 
              className="p-2 -ml-2 hover:bg-gray-100 rounded-full transition-colors"
              data-testid="operators-back-btn"
            >
              <ArrowLeft className="w-5 h-5 text-gray-700" />
            </button>
            <div className="flex-1">
              <h1 className="text-xl font-bold text-gray-900">Gestione {resourceTypeNamePlural}</h1>
              <p className="text-sm text-gray-500">Configura il tuo team e i loro orari</p>
            </div>
            <button
              onClick={openAddModal}
              className="flex items-center gap-2 px-4 py-2 bg-emerald-500 text-white rounded-xl text-sm font-medium hover:bg-emerald-600 transition-colors"
              data-testid="add-operator-btn"
            >
              <Plus className="w-4 h-4" />
              Aggiungi
            </button>
          </div>

          {/* Search */}
          <div className="px-4 pb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={`Cerca ${resourceTypeName.toLowerCase()}...`}
                className="w-full pl-10 pr-4 py-2.5 bg-gray-100 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Operators List */}
      <div className="max-w-screen-2xl mx-auto px-6 py-6">
        {filteredOperators.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredOperators.map((operator) => (
              <div 
                key={operator.id} 
                className={`bg-white rounded-2xl p-4 shadow-sm hover:shadow-md transition-shadow ${!operator.isActive ? 'opacity-60' : ''}`}
              >
                <div className="flex items-start gap-4">
                  <div className={`w-14 h-14 rounded-xl ${getColorClass(operator.color)} flex items-center justify-center text-white font-bold text-lg`}>
                    {operator.avatar || operator.name?.charAt(0)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-gray-900 truncate">
                        {operator.name} {operator.surname}
                      </h3>
                      {!operator.isActive && (
                        <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">Inattivo</span>
                      )}
                    </div>
                    <p className="text-sm text-gray-500">{operator.role || resourceTypeName}</p>
                    {operator.specialties?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {operator.specialties.slice(0, 3).map((spec, idx) => (
                          <span key={idx} className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                            {spec}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Contact Info */}
                <div className="mt-4 pt-4 border-t border-gray-100 space-y-2">
                  {operator.phone && (
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <Phone className="w-4 h-4" />
                      <span>{operator.phone}</span>
                    </div>
                  )}
                  {operator.email && (
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <Mail className="w-4 h-4" />
                      <span className="truncate">{operator.email}</span>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex gap-2 mt-4">
                  <button
                    onClick={() => toggleActive(operator.id)}
                    className={`flex-1 flex items-center justify-center gap-1 py-2 rounded-xl text-sm font-medium transition-colors ${
                      operator.isActive 
                        ? 'bg-gray-100 text-gray-600 hover:bg-gray-200' 
                        : 'bg-emerald-50 text-emerald-600 hover:bg-emerald-100'
                    }`}
                  >
                    {operator.isActive ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    {operator.isActive ? 'Disattiva' : 'Attiva'}
                  </button>
                  <button
                    onClick={() => openEditModal(operator)}
                    className="flex-1 flex items-center justify-center gap-1 bg-emerald-50 text-emerald-600 py-2 rounded-xl text-sm font-medium hover:bg-emerald-100 transition-colors"
                  >
                    <Edit3 className="w-4 h-4" />
                    Modifica
                  </button>
                  <button
                    onClick={() => setShowDeleteConfirm(operator.id)}
                    className="p-2 bg-red-50 text-red-500 rounded-xl hover:bg-red-100 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Users className="w-8 h-8 text-gray-400" />
            </div>
            <p className="text-gray-500 mb-4">
              {searchQuery ? `Nessun ${resourceTypeName.toLowerCase()} trovato` : `Nessun ${resourceTypeName.toLowerCase()} configurato`}
            </p>
            <button
              onClick={openAddModal}
              className="px-6 py-2.5 bg-emerald-500 text-white rounded-xl font-medium hover:bg-emerald-600 transition-colors"
            >
              Aggiungi {resourceTypeName}
            </button>
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-end sm:items-center sm:justify-center" onClick={() => setShowModal(false)}>
          <div className="bg-white w-full sm:max-w-lg max-h-[90vh] overflow-y-auto sm:rounded-2xl rounded-t-3xl" onClick={e => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-gray-100 p-4 flex items-center justify-between">
              <h3 className="text-lg font-bold text-gray-900">
                {editingOperator ? `Modifica ${resourceTypeName}` : `Nuovo ${resourceTypeName}`}
              </h3>
              <button onClick={() => setShowModal(false)} className="p-2 hover:bg-gray-100 rounded-full">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nome *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Nome"
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Cognome</label>
                  <input
                    type="text"
                    value={formData.surname}
                    onChange={(e) => setFormData(prev => ({ ...prev, surname: e.target.value }))}
                    placeholder="Cognome"
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Ruolo</label>
                <input
                  type="text"
                  value={formData.role}
                  onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value }))}
                  placeholder="es. Senior, Junior, Specialista..."
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                />
              </div>

              {/* Contact */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Telefono</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                    placeholder="+39 333 123 4567"
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="email@example.it"
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  />
                </div>
              </div>

              {/* Color */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Colore identificativo</label>
                <div className="flex gap-2">
                  {colorOptions.map((color) => (
                    <button
                      key={color.id}
                      onClick={() => setFormData(prev => ({ ...prev, color: color.id }))}
                      className={`w-10 h-10 rounded-xl ${color.class} flex items-center justify-center transition-transform ${
                        formData.color === color.id ? 'scale-110 ring-2 ring-offset-2 ring-gray-400' : ''
                      }`}
                    >
                      {formData.color === color.id && <Check className="w-5 h-5 text-white" />}
                    </button>
                  ))}
                </div>
              </div>

              {/* Working Hours */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Orari di lavoro</label>
                <div className="space-y-2">
                  {Object.entries(dayNames).map(([dayKey, dayLabel]) => (
                    <div key={dayKey} className="flex items-center gap-3 bg-gray-50 rounded-xl p-3">
                      <button
                        onClick={() => toggleWorkingDay(dayKey)}
                        className={`w-6 h-6 rounded-lg flex items-center justify-center transition-colors ${
                          formData.workingDays[dayKey]?.enabled 
                            ? 'bg-emerald-500 text-white' 
                            : 'bg-gray-200 text-gray-400'
                        }`}
                      >
                        {formData.workingDays[dayKey]?.enabled && <Check className="w-4 h-4" />}
                      </button>
                      <span className={`w-24 text-sm ${formData.workingDays[dayKey]?.enabled ? 'text-gray-900' : 'text-gray-400'}`}>
                        {dayLabel}
                      </span>
                      {formData.workingDays[dayKey]?.enabled && (
                        <div className="flex items-center gap-2 flex-1">
                          <input
                            type="time"
                            value={formData.workingDays[dayKey]?.start || '09:00'}
                            onChange={(e) => updateWorkingHours(dayKey, 'start', e.target.value)}
                            className="px-2 py-1 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500"
                          />
                          <span className="text-gray-400">-</span>
                          <input
                            type="time"
                            value={formData.workingDays[dayKey]?.end || '18:00'}
                            onChange={(e) => updateWorkingHours(dayKey, 'end', e.target.value)}
                            className="px-2 py-1 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500"
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Active Status */}
              <div className="flex items-center justify-between py-2">
                <div>
                  <p className="font-medium text-gray-900">Attivo</p>
                  <p className="text-sm text-gray-500">L'{resourceTypeName.toLowerCase()} può ricevere prenotazioni</p>
                </div>
                <button
                  onClick={() => setFormData(prev => ({ ...prev, isActive: !prev.isActive }))}
                  className={`relative w-12 h-6 rounded-full transition-colors ${formData.isActive ? 'bg-emerald-500' : 'bg-gray-300'}`}
                >
                  <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform shadow ${formData.isActive ? 'translate-x-6' : ''}`} />
                </button>
              </div>
            </div>

            {/* Footer */}
            <div className="sticky bottom-0 bg-white border-t border-gray-100 p-4 flex gap-3">
              <button
                onClick={() => setShowModal(false)}
                className="flex-1 py-3 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-colors"
              >
                Annulla
              </button>
              <button
                onClick={handleSave}
                disabled={!formData.name.trim()}
                className="flex-1 py-3 bg-emerald-500 text-white rounded-xl font-medium hover:bg-emerald-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {editingOperator ? 'Salva modifiche' : 'Aggiungi'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowDeleteConfirm(null)}>
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full" onClick={e => e.stopPropagation()}>
            <div className="text-center">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-6 h-6 text-red-500" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Elimina {resourceTypeName}?</h3>
              <p className="text-gray-500 mb-6">Questa azione non può essere annullata.</p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowDeleteConfirm(null)}
                  className="flex-1 py-2.5 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200"
                >
                  Annulla
                </button>
                <button
                  onClick={() => handleDelete(showDeleteConfirm)}
                  className="flex-1 py-2.5 bg-red-500 text-white rounded-xl font-medium hover:bg-red-600"
                >
                  Elimina
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OperatorsPage;
